





























































import Vue from "vue";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";
import {
  DrillQuestionAndAnswers,
  DrillLearningStatus,
  DrillVocabularyAnswer,
  RespDrillVocabularyAnswer,
  ApiDrillVocabulary,
  ApiDrillVocabularyOption
} from "@tt-app/models/exercise";
import { MCQOptions } from "@joyz/app-core/models/drill";

import IconSpeaker from "@joyz/app-core/assets/icons/transparent/Speaker.svg";
import IconSpeakerActive from "@joyz/app-core/assets/icons/default/Speaker.svg";
import { audioContext } from "@tt-app/utils/audio/audioContext";

interface MCQOptionsWithAudio extends MCQOptions {
  // If defined, display a speaker along the text
  audioInText?: string;
  // If defined, display a speaker along the description
  audioInDescription?: string;
}

interface Data {
  icons: { [key: string]: string };
  // To track which option is currently being played. -1 means no option
  // is currently being played
  playingOptionId: string | number;
}

interface Methods {
  playAudio: (url: string) => void;
  submit: (option: MCQOptionsWithAudio) => void;
}

interface Computed {
  drillQuestion: ApiDrillVocabulary;
  learnStatus: number;
  learnStatusDecrease: boolean | undefined;
  learnStatusIncrease: boolean | undefined;
  questionOptions: MCQOptionsWithAudio[];
  questionCorrectAnswerId: number | undefined;
  questionSelectedId: number | undefined;
  word: string | undefined;
  wordTranslation: string | undefined;
}

interface Props {
  audioDecoderPlayer: AudioDecoderPlayer;
  drillExercise: DrillQuestionAndAnswers;
  isFinished: boolean;
  isLoading: boolean;
}

/**
 * Scoped slot info for jz-multiple-choice-questions:
 *  "props" has two keys: "option" and "icon" if a right icon (correct,
 *  incorrect...) is available
 */
export default Vue.extend<Data, Methods, Computed, Props>({
  name: "DrillQuestionVocabulary",
  components: {},

  props: {
    audioDecoderPlayer: { type: Object as () => AudioDecoderPlayer, required: true },
    drillExercise: { type: Object as () => DrillQuestionAndAnswers, required: true },
    isFinished: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false }
  },

  data() {
    return {
      icons: {
        speaker: IconSpeaker,
        speakerActive: IconSpeakerActive
      },
      playingOptionId: -1
    };
  },

  computed: {
    drillQuestion() {
      return this.drillExercise.question as ApiDrillVocabulary;
    },

    learnStatus() {
      const getStatus = (status: DrillLearningStatus): number => (status !== null ? status : -1);

      return this.drillExercise.lastAnswer
        ? getStatus(this.drillExercise.lastAnswer.learnStatus.newLearnStatus)
        : getStatus(this.drillExercise.question.learnedStatus);
    },

    learnStatusDecrease() {
      return this.drillExercise.lastAnswer
        ? this.drillExercise.lastAnswer.learnStatus.hasIncreased === false
        : false;
    },

    learnStatusIncrease() {
      return this.drillExercise.lastAnswer
        ? this.drillExercise.lastAnswer.learnStatus.hasIncreased === true
        : false;
    },

    questionOptions() {
      // Get the basic info (ID, text) for each option
      let options: MCQOptionsWithAudio[] = this.drillQuestion.options.map(
        (o): MCQOptionsWithAudio => {
          const opt: MCQOptionsWithAudio = { id: o.id, text: o.text };

          // Append audio URL only if question is skipped or answer!
          if (this.drillExercise.skipped || this.drillExercise.lastAnswer) {
            opt.audioInText = o.audio;
          }

          return opt;
        }
      );

      // Get the translation either from user's answer, if defined, or skipped
      // response, if defined
      let translations: ApiDrillVocabularyOption[] = [];

      // Check if question is skipped
      if (this.drillExercise.skipped) {
        const skipped = this.drillExercise.skipped as RespDrillVocabularyAnswer;
        translations = skipped.translations.options || [];
      }

      // Check if user has submitted an answer
      const lastAnswer = this.drillExercise.lastAnswer;
      if (lastAnswer) {
        const lastCorrection = lastAnswer.correction as RespDrillVocabularyAnswer;
        translations = lastCorrection.translations.options || [];
      }

      options = options.map(
        (o): MCQOptionsWithAudio => {
          const translation = translations.find(t => t.id === o.id);

          if (!translation) {
            return o;
          }

          return {
            ...o,
            description: translation.text ? translation.text : undefined,
            audioInDescription: translation.audio
          };
        }
      );

      return options;
    },

    questionCorrectAnswerId() {
      if (this.drillExercise.skipped) {
        const correctId = (this.drillExercise.skipped as RespDrillVocabularyAnswer).correctId;
        return correctId;
      }

      if (this.drillExercise.lastAnswer) {
        const correction = this.drillExercise.lastAnswer.correction as RespDrillVocabularyAnswer;
        return correction.correctId;
      }

      return undefined;
    },

    questionSelectedId() {
      if (this.drillExercise.lastAnswer) {
        const submittedAnswer = this.drillExercise.lastAnswer
          .submittedAnswer as DrillVocabularyAnswer;
        return submittedAnswer.optionId;
      }

      return undefined;
    },

    word() {
      return this.drillQuestion.word.spelling;
    },

    wordTranslation() {
      if (this.drillExercise.skipped) {
        const translations = (this.drillExercise.skipped as RespDrillVocabularyAnswer).translations;
        return translations.word;
      }

      const lastAnswer = this.drillExercise.lastAnswer;
      if (!lastAnswer) {
        return undefined;
      }

      const lastCorrection = lastAnswer.correction as RespDrillVocabularyAnswer;
      return lastCorrection.translations.word;
    }
  },

  watch: {
    "drillExercise.question.uuid": {
      handler(newVal: string, oldVal: string) {
        if (newVal !== oldVal) {
          const mainContainer = document.querySelector(".drill-main-container");
          if (mainContainer) {
            mainContainer.scrollTo(0, 0);
          }
        }
      },
      immediate: false
    }
  },

  methods: {
    async playAudio(url) {
      if (this.audioDecoderPlayer.status === "playing") {
        this.audioDecoderPlayer.stop();
      }

      await audioContext.resume();
      await this.audioDecoderPlayer.play(url, {
        onEnded: () => {
          this.playingOptionId = -1;
        }
      });
    },

    async submit(option) {
      // The question is already answer or finished (skipped)
      if (this.drillExercise.lastAnswer || this.isFinished) {
        const audioUrl = option.audioInText || option.audioInDescription;
        if (audioUrl) {
          this.playingOptionId = option.id;
          this.playAudio(audioUrl);
        }

        return;
      }

      // Submit the selected answer
      const answer: DrillVocabularyAnswer = {
        type: "DRILL_VOCABULARY",
        optionId: option.id as number,
        uuid: this.drillExercise.question.uuid
      };
      this.$emit("on-user-answer", answer);
    }
  }
});
